import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import type { ApprovalRequest } from '@paradigm/terminal-core';

import { MONO_BOX } from '../../styles/mono-box';

interface Props {
  readonly approval: ApprovalRequest;
  readonly onRespond: (approvalId: string, approved: boolean) => void;
}

export default function ApprovalCard({ approval, onRespond }: Props) {
  const handleApprove = useCallback(() => {
    onRespond(approval.approvalId, true);
  }, [approval.approvalId, onRespond]);

  const handleDeny = useCallback(() => {
    onRespond(approval.approvalId, false);
  }, [approval.approvalId, onRespond]);

  const isPending = approval.state === 'pending';

  return (
    <Card $state={approval.state}>
      <Header>
        <StatusIcon $state={approval.state}>
          {approval.state === 'approved'
            ? '✓'
            : approval.state === 'denied'
              ? '✗'
              : '⚠'}
        </StatusIcon>
        <Title>
          {approval.state === 'approved'
            ? 'Approved'
            : approval.state === 'denied'
              ? 'Denied'
              : 'Approval Required'}
        </Title>
      </Header>
      {approval.description != null && approval.description.length > 0 && (
        <Description>{approval.description}</Description>
      )}
      <CommandBlock>{approval.command}</CommandBlock>
      {isPending ? (
        <ButtonRow>
          <ApproveButton onClick={handleApprove}>Approve</ApproveButton>
          <DenyButton onClick={handleDeny}>Deny</DenyButton>
        </ButtonRow>
      ) : null}
    </Card>
  );
}

const Card = styled.div<{ $state: ApprovalRequest['state'] }>`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $state }) =>
      $state === 'approved'
        ? 'var(--accent-color, #1bc1cf)'
        : $state === 'denied'
          ? 'var(--error-color, #e3415a)'
          : 'var(--pending-color, #ff9800)'};
  background: ${({ $state }) =>
    $state === 'approved'
      ? 'color-mix(in srgb, var(--accent-color) 6%, transparent)'
      : $state === 'denied'
        ? 'color-mix(in srgb, var(--error-color) 6%, transparent)'
        : 'color-mix(in srgb, var(--pending-color, #ff9800) 6%, transparent)'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const StatusIcon = styled.span<{ $state: ApprovalRequest['state'] }>`
  font-size: 0.875rem;
  ${({ $state }) =>
    $state === 'approved'
      ? css`
          color: var(--accent-color, #1bc1cf);
        `
      : $state === 'denied'
        ? css`
            color: var(--error-color, #e3415a);
          `
        : css`
            color: var(--pending-color, #ff9800);
          `}
`;

const Title = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Description = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  margin-bottom: 6px;
  line-height: 1.4;
`;

const CommandBlock = styled.pre`
  ${MONO_BOX}
  font-size: 0.6875rem;
  line-height: 1.5;
  padding: 6px 10px;
  white-space: pre-wrap;
  word-break: break-all;
  color: var(--text-primary);
  margin: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const baseButton = css`
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const ApproveButton = styled.button`
  ${baseButton}
  background: var(--accent-color, #1bc1cf);
  color: var(--cta-text-color, #090a0a);
`;

const DenyButton = styled.button`
  ${baseButton}
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);

  &:hover {
    color: var(--text-primary);
  }
`;
